import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { currentVersion, fetchLatestVersion, reloadPage } from '../../utils/appVersion';
import './AppUpdate.css';

/**
 * Keeps every open tab on the latest deploy. Checks index.html now and then;
 * when a newer build is out it shows a small "Refresh" bar and reloads on the
 * next in-app navigation (the user is leaving that page anyway, so nothing
 * half-typed is lost).
 *
 * The KDS board is left alone — it already restarts itself nightly when idle
 * (KdsBoardTab) and a bar over tickets mid-service would be in the way — and
 * so is the unattended order-status screen.
 */

const CHECK_EVERY_MS = 10 * 60 * 1000;
const MIN_GAP_MS = 2 * 60 * 1000;
const FIRST_CHECK_MS = 20 * 1000;
const QUIET_ROUTES = ['/tickets', '/order-status'];

export default function AppUpdate() {
  const { pathname } = useLocation();
  const [ready, setReady] = useState(false);
  const [running] = useState(() => currentVersion());
  const lastPath = useRef(pathname);

  useEffect(() => {
    if (!running) return undefined; // development build
    let stopped = false;
    let lastCheck = 0;
    const check = async () => {
      if (stopped || document.visibilityState === 'hidden' || Date.now() - lastCheck < MIN_GAP_MS) return;
      lastCheck = Date.now();
      try {
        const latest = await fetchLatestVersion();
        if (!stopped && latest && latest !== running) setReady(true);
      } catch { /* offline — try again later */ }
    };
    const first = setTimeout(check, FIRST_CHECK_MS);
    const timer = setInterval(check, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', check);
    return () => {
      stopped = true;
      clearTimeout(first);
      clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, [running]);

  // The router already moved to the new path; a reload loads it on the new build.
  useEffect(() => {
    const moved = pathname !== lastPath.current;
    lastPath.current = pathname;
    if (ready && moved) reloadPage();
  }, [ready, pathname]);

  if (!ready || QUIET_ROUTES.some((p) => pathname.startsWith(p))) return null;
  return (
    <div className="appupd" role="status">
      <span>🔄 A new version of the app is available</span>
      <button type="button" className="appupd__btn" onClick={reloadPage}>Refresh</button>
    </div>
  );
}
